import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import useAuth from '@/hooks/stores/useAuth';
import { userService } from '@/services/userService';

// Confirmation modal component
export const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-auto p-6 relative min-h-fit">
        <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 mb-6">{message}</p>
        <div className="flex flex-col sm:flex-row justify-end gap-3">
          <button 
            onClick={onClose}
            disabled={isLoading}
            className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 order-2 sm:order-1"
          >
            Cancel
          </button>
          <button 
            onClick={handleConfirm}
            disabled={isLoading}
            className="w-full sm:w-auto relative px-4 py-2 bg-[#F16745] text-white rounded-md hover:bg-[#E55D3D] disabled:opacity-50 min-w-[100px] order-1 sm:order-2"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-t-2 border-white rounded-full animate-spin"></div>
              </div>
            ) : (
              'Confirm'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// User actions dropdown component
export const UserActions = ({ userId, onDelete, onStatusChange }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showDeceasedModal, setShowDeceasedModal] = useState(false);
  const navigate = useNavigate();
  const { userRole } = useAuth(); // Get the user role
  
  const trigger = useRef(null);
  const dropdown = useRef(null);

  // Function to mark user as deceased
  const markUserAsDeceased = async () => {
    try {
      const response = await userService.markUserAsDeceased(userId);
      console.log('Deceased API Response:', response);
      
      // Show success toast
      toast.success('User has been marked as deceased', {
        style: {
          background: '#F16745',
          color: 'white',
          borderRadius: '8px',
        },
      });

      // Close the modal
      setShowDeceasedModal(false);

      // Call onStatusChange to trigger refetch
      if (onStatusChange) {
        await onStatusChange();
      }
      
    } catch (error) {
      console.error('Error marking user as deceased:', error);
      
      // Handle various error responses
      if (error.response) {
        const errorMessage = error.response.data?.message || 'An error occurred';
        
        if (error.response.status === 404) {
          // Close the modal first
          setShowDeceasedModal(false);
          
          toast.error('This user has no executor... Email not sent', {
            style: {
              background: '#F16745',
              color: 'white',
              borderRadius: '8px',
            },
          });
          // Trigger table refresh
          if (onStatusChange) {
            await onStatusChange();
          }
        } else {
          // Close the modal for other errors too
          setShowDeceasedModal(false);
          
          toast.error(errorMessage, {
            style: {
              background: '#F16745',
              color: 'white',
              borderRadius: '8px',
            },
          });
        }
      } else {
        // Close the modal for network/other errors
        setShowDeceasedModal(false);
        
        toast.error('An error occurred while marking user as deceased', {
          style: {
            background: '#F16745',
            color: 'white',
            borderRadius: '8px',
          },
        });
      }
    }
  };

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!dropdown.current) return;
      if (
        !dropdownOpen ||
        dropdown.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setDropdownOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  return (
    <div className="relative inline-block">
      <button
        ref={trigger}
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className="text-body-color dark:text-dark-6 p-1"
      >
        <svg
          width={24}
          height={24}
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10 12C10 10.8954 10.8954 10 12 10C13.1046 10 14 10.8954 14 12C14 13.1046 13.1046 14 12 14C10.8954 14 10 13.1046 10 12Z"
            fill="currentColor"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10 5C10 3.89543 10.8954 3 12 3C13.1046 3 14 3.89543 14 5C14 6.10457 13.1046 7 12 7C10.8954 7 10 6.10457 10 5Z"
            fill="currentColor"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10 19C10 17.8954 10.8954 17 12 17C13.1046 17 14 17.8954 14 19C14 20.1046 13.1046 21 12 21C10.8954 21 10 20.1046 10 19Z"
            fill="currentColor"
          />
        </svg>
      </button>

      {dropdownOpen && (
        <div
          ref={dropdown}
          className="fixed z-70 w-[120px] rounded-md bg-white p-2 shadow-lg dark:bg-dark border-stroke dark:border-dark-3 border"
          style={{
            top: trigger.current ? trigger.current.getBoundingClientRect().bottom + 5 : 0,
            left: trigger.current ? trigger.current.getBoundingClientRect().left - 100 : 0
          }}
        >
          {/* Only show Edit button for non-manager users */}
          {!(userRole?.toLowerCase() === 'manager' || userRole?.toLowerCase() === 'bereavement team') && (
            <button 
              onClick={() => {
                navigate(`/users/edit/${userId}`);
                setDropdownOpen(false);
              }}
              className="w-full px-5 py-2 text-sm text-left rounded text-body-color hover:bg-[#F16745] hover:text-white dark:text-dark-6 dark:hover:bg-dark-2"
            >
              Edit
            </button>
          )}
          
          {/* Only show Delete button for non-manager users */}
          {!(userRole?.toLowerCase() === 'manager' || userRole?.toLowerCase() === 'bereavement team') && (
            <button 
              onClick={() => {
                onDelete();
                setDropdownOpen(false);
              }}
              className="w-full px-5 py-2 text-sm text-left rounded text-body-color hover:bg-[#F16745] hover:text-white dark:text-dark-6 dark:hover:bg-dark-2"
            >
              Delete
            </button>
          )}
          
          {/* Show deceased option for both admin and manager roles */}
          {(userRole?.toLowerCase() === 'manager' || userRole?.toLowerCase() === 'admin' || userRole?.toLowerCase() === 'bereavement team') && (
            <button 
              onClick={() => {
                setShowDeceasedModal(true);
                setDropdownOpen(false);
              }}
              className="w-full px-5 py-2 text-sm text-left rounded text-body-color hover:bg-[#F16745] hover:text-white dark:text-dark-6 dark:hover:bg-dark-2"
            >
              Deceased
            </button>
          )}
        </div>
      )}
      
      {/* Confirmation Modal */}
      <ConfirmationModal 
        isOpen={showDeceasedModal}
        onClose={() => setShowDeceasedModal(false)}
        onConfirm={markUserAsDeceased}
        title="Mark User as Deceased"
        message="Are you sure you want to mark this user as deceased? This action cannot be undone."
      />
    </div>
  );
};

export default UserActions; 